import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { ArrowUpRight, Search, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { HeaderSearchRecord } from 'src/contracts/headerSearch';
import { useCompanyProfileWorkspaceScope } from 'src/workspaces/CompanyProfileWorkspaceScope';
import {
  getCompanyDataTable,
  searchCompanyRecords
} from 'src/queries/companyProfile/api';
import { companyWorkspacePath } from 'src/workspaces/tenantWorkspace';
import {
  recordDestination,
  recordDestinationLabel,
  searchAccessLevels,
  searchFeatures
} from './searchDestinations';

const refine = (
  <Typography variant="caption">
    More matches are available. Refine your search to narrow the results.
  </Typography>
);
export default function HeaderSearch() {
  const workspace = useCompanyProfileWorkspaceScope();
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [query, setQuery] = useState('');
  const scopeKey = JSON.stringify([
    workspace?.routeBase,
    workspace?.company.id,
    workspace?.actorId,
    workspace?.role,
    workspace?.accessLevel,
    workspace?.accessLevels
  ]);
  useEffect(() => {
    const timer = window.setTimeout(() => setQuery(input.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [input]);
  useEffect(() => {
    setInput('');
    setQuery('');
  }, [scopeKey]);
  const canOpenWorkers = Boolean(
    workspace &&
      (workspace.role === 'SUPER_ADMIN' ||
        searchAccessLevels(workspace).includes('COMPANY_ADMIN'))
  );
  const results = useQuery({
    queryKey: ['authorized-header-search', scopeKey, query],
    queryFn: async () => {
      const [records, workers] = await Promise.all([
        searchCompanyRecords(workspace!, query),
        canOpenWorkers
          ? getCompanyDataTable(
              workspace!,
              workspace!.company.id,
              'workers',
              query
            )
          : Promise.resolve(null)
      ]);
      return { records, workers };
    },
    enabled: open && Boolean(workspace) && query.length >= 2,
    retry: false,
    staleTime: 0,
    cacheTime: 0
  });
  const waiting = input.trim() !== query || results.isFetching;
  const data =
    !waiting && !results.isError && query.length >= 2
      ? results.data
      : undefined;
  const features =
    workspace && !waiting ? searchFeatures(workspace, query) : [];
  const row = (
    key: string,
    title: string,
    context: string,
    destination: string,
    to: string
  ) => (
    <ListItemButton
      component={Link}
      to={to}
      key={key}
      onClick={() => setOpen(false)}
      sx={{
        borderRadius: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        gap: 2,
        py: 1
      }}
    >
      <ListItemText
        primary={title}
        secondary={context}
        primaryTypographyProps={{ fontWeight: 600 }}
        sx={{ minWidth: 0, overflowWrap: 'anywhere' }}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          color: 'text.secondary',
          flexShrink: 0
        }}
      >
        <Typography variant="caption">{destination}</Typography>
        <ArrowUpRight size={14} />
      </Box>
    </ListItemButton>
  );
  const recordRow = (record: HeaderSearchRecord) =>
    row(
      `${record.kind}-${record.id}`,
      record.title,
      record.context,
      recordDestinationLabel[record.kind],
      recordDestination(workspace!, record)
    );
  return (
    <>
      <Tooltip title="Search">
        <button
          type="button"
          aria-label="Search"
          onClick={() => setOpen(true)}
          className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-white/10 bg-white/[0.03] text-white/[0.72] transition-all duration-200 hover:bg-white/[0.08] hover:text-white"
        >
          <Search size={18} />
        </button>
      </Tooltip>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="md"
        aria-labelledby="app-search-title"
      >
        <DialogTitle
          id="app-search-title"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            bgcolor: '#06070A',
            color: 'common.white',
            gap: 2
          }}
        >
          Search {workspace?.company.name ?? 'workspace'}
          <IconButton color="inherit" aria-label="Close search" onClick={() => setOpen(false)}>
            <X size={20} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: '20px !important' }}>
          {!workspace ? (
            <Alert severity="info">
              Open a company workspace to search the records available to you.
            </Alert>
          ) : (
            <>
              <TextField
                autoFocus
                fullWidth
                label="Search records and features"
                value={input}
                onChange={(event) => setInput(event.target.value)}
                inputProps={{ maxLength: 100 }}
                placeholder="Enter a client, event, job number or feature"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Results available to you in this workspace.
              </Typography>
              {input.trim().length < 2 ? (
                <Typography sx={{ py: 3 }}>
                  Enter at least two characters to search.
                </Typography>
              ) : waiting ? (
                <Box role="status" sx={{ py: 3, display: 'flex', gap: 1 }}>
                  <CircularProgress size={20} />
                  Searching…
                </Box>
              ) : (
                <Box aria-live="polite">
                  {results.isError && (
                    <Alert
                      severity="error"
                      sx={{ mt: 2 }}
                      action={
                        <Button onClick={() => void results.refetch()}>
                          Retry
                        </Button>
                      }
                    >
                      Record search is unavailable. Please try again.
                    </Alert>
                  )}
                  {data &&
                    data.records.clients.length +
                      data.records.events.length +
                      (data.workers?.rows.length ?? 0) +
                      features.length ===
                      0 && (
                      <Typography sx={{ py: 3 }}>
                        No matching records or features.
                      </Typography>
                    )}
                  {Boolean(data?.records.clients.length) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography component="h2" variant="subtitle2">
                        Clients
                      </Typography>
                      {data!.records.clients.map((client) => (
                        <Box key={client.id}>
                          <List dense>
                            {row(
                              `client-${client.id}`,
                              client.title,
                              client.context,
                              'Overview',
                              companyWorkspacePath(
                                workspace,
                                `profile/client-profiles/${client.id}/overview`
                              )
                            )}
                          </List>
                          {client.related.length > 0 && (
                            <Box
                              sx={{
                                ml: 4,
                                pl: 1,
                                borderLeft: '2px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                Related records
                              </Typography>
                              <List dense>{client.related.map(recordRow)}</List>
                              <Button
                                component={Link}
                                to={companyWorkspacePath(
                                  workspace,
                                  `profile/client-profiles/${client.id}/overview`
                                )}
                                onClick={() => setOpen(false)}
                                size="small"
                              >
                                View all related records{' '}
                                <ArrowUpRight size={14} />
                              </Button>
                              {client.moreRelated && (
                                <Typography variant="caption" display="block">
                                  Showing recent related records.
                                </Typography>
                              )}
                            </Box>
                          )}
                        </Box>
                      ))}
                      {data!.records.moreClients && refine}
                    </Box>
                  )}
                  {Boolean(data?.records.events.length) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography component="h2" variant="subtitle2">
                        Jobs &amp; Events
                      </Typography>
                      <List dense>{data!.records.events.map(recordRow)}</List>
                      {data!.records.moreEvents && refine}
                    </Box>
                  )}
                  {Boolean(data?.workers?.rows.length) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography component="h2" variant="subtitle2">
                        Workers
                      </Typography>
                      <List dense>
                        {data!.workers!.rows.slice(0, 8).map((worker) =>
                          row(
                            `worker-${worker.id}`,
                            String(worker.values[0] ?? ''),
                            worker.values
                              .slice(1, 3)
                              .map((value) => String(value ?? ''))
                              .join(' · '),
                            'Worker Profile',
                            workspace.role === 'SUPER_ADMIN'
                              ? `/platform/workforce/${worker.entityId}`
                              : companyWorkspacePath(
                                  workspace,
                                  `workers/${worker.entityId}`
                                )
                          )
                        )}
                      </List>
                      {data!.workers!.rows.length > 8 && refine}
                    </Box>
                  )}
                  {features.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography component="h2" variant="subtitle2">
                        Features
                      </Typography>
                      <List dense>
                        {features.map((feature) =>
                          row(
                            feature.id,
                            feature.title,
                            'Company Profile',
                            feature.title,
                            feature.to
                          )
                        )}
                      </List>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
